import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LoginType, useSendSecureCodeMutation } from '@websky/graphql';
import { useAuthenticate } from '@features/auth';
import LoginTypeSelector from './LoginTypeSelector/LoginTypeSelector';
import { useTheme } from '../../../../theme';
import { LoginData, LoginTypeWithValue, Steps } from '../../../types';
import { getLoginTypeValue } from '../../../utils';
import { AUTH_DUPLICATES, removeFromSessionStorage } from '../../../../cache';
import { SafeHtml } from '@shared/ui';
import { default as BaseButton } from '../../../../BaseComponents/Button';

export interface Props {
	loginTypes: LoginTypeWithValue[];
	login: string;
	code: string;
	stayLoggedIn?: boolean;
	onSuccess: () => void;
	onError: (error: string) => void;
	setLoginData: React.Dispatch<React.SetStateAction<LoginData>>;
	setStep: React.Dispatch<React.SetStateAction<Steps>>;
}

const LoginTypeSelectorForm: React.FC<Props> = ({
	loginTypes,
	login,
	code,
	stayLoggedIn,
	onSuccess,
	onError,
	setLoginData,
	setStep
}: Props) => {
	const { LoginTypeSelectorForm: css } = useTheme('LoginPage');
	const { t } = useTranslation('LoginPage');

	const [loginType, setLoginType] = useState(LoginType.Email);

	const [authenticate, { loading: authenticateLoading }] = useAuthenticate();
	const [sendCode, { loading: codeSending }] = useSendSecureCodeMutation();

	const onSubmit = async () => {
		if (loginType === LoginType.FFP) {
			removeFromSessionStorage(AUTH_DUPLICATES);

			try {
				await authenticate({
					variables: {
						params: {
							login: login,
							loginType: loginType,
							secureCode: code,
							oneSession: !stayLoggedIn
						}
					}
				});

				onSuccess();
			} catch (e) {
				onError('');
			}
		} else if (loginType === LoginType.Email) {
			const email = getLoginTypeValue(loginTypes, LoginType.Email);

			setLoginData({
				login: email,
				loginType: LoginType.Email
			});

			try {
				const data = await sendCode({
					variables: {
						params: {
							login: email,
							loginType: loginType
						}
					}
				});

				if (data?.data?.SendSecureCode?.result) {
					setStep(Steps.EnterCode);
				} else {
					onError('');
				}
			} catch (e) {
				onError('');
			}
		}
	};

	const onBack = () => {
		removeFromSessionStorage(AUTH_DUPLICATES);
		setLoginData({ login: '', loginType: undefined });
		setStep(Steps.LogIn);
	};

	return (
		<div className={css.root}>
			<div className={css.title}>{t('Choose an account to enter')}</div>

			<div className={css.content}>
				<SafeHtml
					tag="span"
					className={css.text}
					html={t('We found an account created with the email', {
						email: getLoginTypeValue(loginTypes, LoginType.Email)
					})}
				/>

				<div className={css.types}>
					{loginTypes.map(item => (
						<LoginTypeSelector
							key={item.type}
							type={item.type}
							value={item.value}
							selected={item.type === loginType}
							onClick={() => setLoginType(item.type)}
						/>
					))}
				</div>

				<div className={css.footer}>
					<BaseButton variant="text" onClick={onBack}>
						{t('Back')}
					</BaseButton>

					<BaseButton variant={'action'} onClick={onSubmit} isLoading={authenticateLoading || codeSending}>
						{t('Continue')}
					</BaseButton>
				</div>
			</div>
		</div>
	);
};

export default LoginTypeSelectorForm;
